import React, { useState, KeyboardEvent, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'
import {
  FaEnvelope,
  FaRegClock,
  FaSms,
  FaTelegramPlane,
  FaVoicemail,
  FaWeebly,
  FaWhatsapp,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { PhoneMockup } from './phone-mock'
import { Template } from '../../../pages/create-campaign/types'
import { CampaignData } from '../../types'
import { SingleCampaignResponse, WalletBalanceData } from '../../../pages/campaign/types'
import useRequest from '../../hook/use-req'
import { toast } from 'sonner'

// ─── Types ────────────────────────────────────────────────────────────────────

type ScheduleMode = 'now' | 'later'

export interface ScheduleLaunchFormData {
  tags: string[]
  scheduleMode: ScheduleMode
  sendTime: string
  timeZone: string
  launch: boolean
}

interface ScheduleLaunchProps {
  campaignData: CampaignData;
  walletBallance: WalletBalanceData | null;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
    onBack: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TIME_ZONES = [
  { value: 'Africa/Lagos', label: 'Africa/Lagos (UTC +01:00)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC +05:30)' },
  { value: 'UTC', label: 'UTC (UTC +00:00)' },
  { value: 'America/New_York', label: 'America/New_York (UTC -05:00)' },
  { value: 'Europe/London', label: 'Europe/London (UTC +00:00)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC -08:00)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC +09:00)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC +11:00)' },
]

// ─── Channel Icons ────────────────────────────────────────────────────────────

const ChannelIcon: React.FC<{ channel: string }> = ({ channel }) => {
  const getIcon = (): IconType => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
        return FaWhatsapp;
      case 'sms':
        return FaSms;
      case 'email':
        return FaEnvelope;
      case 'voice':
        return FaVoicemail;
      case 'rcs':
        return FaRegClock;
      case 'webchat':
        return FaWeebly;
      case 'telegram':
        return FaTelegramPlane;
      default:
        return FaSms;
    }
  };

  const Icon = getIcon() as React.ComponentType<{ className?: string }>;



  return (
    <span className={`flex items-center justify-center text-[#72C078] rounded-full  shrink-0`}>
      <Icon className='w-5 h-5' />
    </span>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface InfoRowProps {
  left: { label: string; value: React.ReactNode }
  right: { label: string; value: React.ReactNode }
}

const InfoRow: React.FC<InfoRowProps> = ({ left, right }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{left.label}</p>
      <div className="text-sm font-semibold text-gray-900">{left.value}</div>
    </div>
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{right.label}</p>
      <div className="text-sm font-semibold text-gray-900">{right.value}</div>
    </div>
  </div>
)

const Divider = () => <div className="border-t border-gray-100" />

// ─── BroadcastTags ────────────────────────────────────────────────────────────

interface BroadcastTagsProps {
  tags: string[]
  onChange: (tags: string[]) => void
}

const BroadcastTags: React.FC<BroadcastTagsProps> = ({ tags, onChange }) => {
  const [input, setInput] = useState('')

  const addTag = (value: string) => {
    const trimmed = value.trim().replace(/^#*/, '')
    if (trimmed && !tags.includes(`#${trimmed}`)) {
      onChange([...tags, `#${trimmed}`])
    }
    setInput('')
  }

  const removeTag = (tag: string) => onChange(tags.filter((t) => t !== tag))

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(input)
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Broadcast Tags
      </p>
      <div
        className="flex flex-wrap gap-2 items-center min-h-[44px] w-full rounded-lg border-2 border-violet-400 bg-white px-3 py-2 cursor-text focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 transition-all"
        onClick={() => document.getElementById('tag-input')?.focus()}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold px-2 py-1"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag) }}
              className="text-violet-400 hover:text-violet-700 transition-colors"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id="tag-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input && addTag(input)}
          placeholder={tags.length === 0 ? 'Add tags...' : ''}
          className="flex-1 min-w-[80px] outline-none text-sm text-gray-700 bg-transparent placeholder-gray-400"
        />
      </div>
      <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add a tag</p>
    </div>
  )
}

// ─── ScheduleOptions ──────────────────────────────────────────────────────────

interface ScheduleOptionsProps {
  mode: ScheduleMode
  onModeChange: (mode: ScheduleMode) => void
  selectedDateTime: string
  onDateTimeChange: (value: string) => void
  timeZone: string
  onTimeZoneChange: (value: string) => void
}

const ScheduleOptions: React.FC<ScheduleOptionsProps> = ({
  mode, onModeChange, selectedDateTime, onDateTimeChange, timeZone, onTimeZoneChange,
}) => {
  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Schedule</p>

      <div className="grid grid-cols-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={() => onModeChange('now')}
          className={`py-2.5 text-sm font-semibold transition-all ${mode === 'now' ? 'bg-white text-[#6366F1] shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
        >
          Send Now
        </button>
        <button
          type="button"
          onClick={() => onModeChange('later')}
          className={`py-2.5 text-sm font-semibold transition-all ${mode === 'later' ? 'bg-[#fff] text-[#6366F1] shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
        >
          Schedule Later
        </button>
      </div>

      {mode === 'later' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Date & Time
            </label>
            <input
              type="datetime-local"
              value={selectedDateTime}
              onChange={(e) => onDateTimeChange(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Time Zone
            </label>
            <div className="relative">
              <select
                value={timeZone}
                onChange={(e) => onTimeZoneChange(e.target.value)}
                className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2.5 pr-8 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all cursor-pointer"
              >
                {TIME_ZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── FormActions ──────────────────────────────────────────────────────────────

interface FormActionsProps {
  onBack: () => void
  onSaveAsDraft: () => void
  onScheduleAndLaunch: () => void
  isLoading?: boolean
}

const FormActions: React.FC<FormActionsProps> = ({
  onBack, onSaveAsDraft, onScheduleAndLaunch, isLoading = false,
}) => (
  <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
    <button
      type="button"
      onClick={onSaveAsDraft}
      className="text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors py-2 px-1 text-center sm:text-left"
    >
      Save as Draft
    </button>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        Back
      </button>
      <button
        type="button"
        onClick={onScheduleAndLaunch}
        disabled={isLoading}
        className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-[#4338CA] hover:bg-violet-700 active:bg-violet-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
      >
        {isLoading ? 'Launching...' : 'Schedule and Launch'}
      </button>
    </div>
  </div>
)

// ─── Main Component ───────────────────────────────────────────────────────────

const ScheduleLaunch: React.FC<ScheduleLaunchProps> = ({
  campaignData,
  walletBallance,
  singleCampaign,
  onSubmit,
    onBack,
}) => {
  const token = localStorage.getItem("authToken");
  const campaignId = localStorage.getItem("campaignId");

  const [tags, setTags] = useState<string[]>(() => {
    return campaignData.tags || [];
  });

  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');

  // For scheduled later, store local datetime in YYYY-MM-DDThh:mm format
  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    // Default to tomorrow at 9:00 AM in local time
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return tomorrow.toISOString().slice(0, 16);
  });

  const [timeZone, setTimeZone] = useState('Africa/Lagos');

  const [isLoading, setIsLoading] = useState(false);

  const { makeRequest: launchCampaign } = useRequest("/campaign/update", "PUT", {
    Authorization: `Bearer ${token}`,
  });

  // Format data from campaignData
  const broadcastName = campaignData.campaign_name || singleCampaign?.campaign_name || 'Untitled Campaign';
  const broadcastGoal = campaignData.campaign_goal || singleCampaign?.campaign_goal || 'No goal specified';
  const channel = campaignData.campaign_channel || singleCampaign?.campaign_channel || 'WhatsApp';
  const senderProfile = campaignData.sender_name || singleCampaign?.sender_name || '+123 - 9900990099';

  // Calculate costs
  const walletBalance = walletBallance?.balance ? `$${walletBallance.balance}` : 'Insufficient';
  const walletCurrency = walletBallance?.currency || 'NGN';
  const formatMoney = (amount: number | string) => {
    const value = typeof amount === "string" ? parseFloat(amount) : amount;
    return `${walletCurrency} ${value.toFixed(2)}`;
  };

  const audienceSize = campaignData.audienceSize || singleCampaign?.audience_size || 0;
  const costPerUnit = 0.05;
  const estimatedCost = (parseInt(audienceSize.toString()) * costPerUnit).toFixed(2);
  const totalCost = formatMoney(estimatedCost);
  const estimatedReach = `${audienceSize} Contacts`;

  const getFormData = (): ScheduleLaunchFormData => ({
    tags,
    scheduleMode,
    sendTime: selectedDateTime,
    timeZone,
    launch: true
  });

  const handleLaunch = async () => {
    setIsLoading(true);

    const formData = getFormData();

    // Convert to UTC timestamp if scheduled later
    let scheduledStartTimestamp = null;
    if (scheduleMode === 'later' && selectedDateTime) {
      const localDateTime = new Date(selectedDateTime);
      scheduledStartTimestamp = Math.floor(localDateTime.getTime() / 1000);
    }

    // Get all audience IDs from campaignData
    const allAudienceIds = [
      ...(campaignData.selectedAudiences?.map(a => a.audience_id) || []),
      campaignData.uploadAudienceId,
      campaignData.manualAudienceId
    ].filter(Boolean) as string[];

    // Prepare the launch payload
    const launchPayload = {
      campaign_id: campaignId,
      audience_ids: allAudienceIds.length > 0 ? allAudienceIds : [],
      group_ids: [],
      scheduled_start: scheduledStartTimestamp,
      timezone: timeZone,
      tags: tags.map(tag => tag.replace('#', '')),
      dnd: false,
      campaign_size: parseInt(audienceSize.toString()),
      estimated_cost: parseFloat(estimatedCost),
      launch: true,
      content: campaignData.content || {
        template_id: "",
        template_name: "",
        variables: {},
        header_type: "none",
        header_value: "",
        message: campaignData.messages || "",
        footer: "Reply STOP to unsubscribe",
        buttons: campaignData.link ? [{
          type: "URL",
          text: "Learn More",
          url: campaignData.link
        }] : []
      }
    };



    const [response] = await launchCampaign(launchPayload);

    if (response?.status) {
      toast.success(response?.message || "Campaign launched successfully!");

      onSubmit({
        ...campaignData,
        scheduled_start: scheduledStartTimestamp ? new Date(scheduledStartTimestamp * 1000).toISOString() : null,
        timezone: timeZone,
        tags: tags,
        send_now: scheduleMode === 'now'
      });

      // Clear localStorage
      localStorage.removeItem("draftCampaignId");
      localStorage.removeItem("draftAudienceId");
      localStorage.removeItem("campaignId");
      localStorage.removeItem("audienceId");

    } else {
      toast.error(response?.detail || "Failed to launch campaign");
      setIsLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    setIsLoading(true);

    const formData = getFormData();

    // Convert to UTC timestamp if scheduled later
    let scheduledStartTimestamp = null;
    if (scheduleMode === 'later' && selectedDateTime) {
      const localDateTime = new Date(selectedDateTime);
      scheduledStartTimestamp = Math.floor(localDateTime.getTime() / 1000);
    }

    // Get all audience IDs from campaignData
    const allAudienceIds = [
      ...(campaignData.selectedAudiences?.map(a => a.audience_id) || []),
      campaignData.uploadAudienceId,
      campaignData.manualAudienceId
    ].filter(Boolean) as string[];

    // Prepare the launch payload
    const launchPayload = {
      campaign_id: campaignId,
      audience_ids: allAudienceIds.length > 0 ? allAudienceIds : [],
      group_ids: [],
      scheduled_start: scheduledStartTimestamp,
      timezone: timeZone,
      tags: tags.map(tag => tag.replace('#', '')),
      dnd: false,
      campaign_size: parseInt(audienceSize.toString()),
      estimated_cost: parseFloat(estimatedCost),
      content: campaignData.content || {
        template_id: "",
        template_name: "",
        variables: {},
        header_type: "none",
        header_value: "",
        message: campaignData.messages || "",
        footer: "Reply STOP to unsubscribe",
        buttons: campaignData.link ? [{
          type: "URL",
          text: "Learn More",
          url: campaignData.link
        }] : []
      }
    };



    const [response] = await launchCampaign(launchPayload);

    if (response?.status) {
      toast.success(response?.message || "Campaign launched successfully!");

      onSubmit({
        ...campaignData,
        scheduled_start: scheduledStartTimestamp ? new Date(scheduledStartTimestamp * 1000).toISOString() : null,
        timezone: timeZone,
        tags: tags,
        send_now: scheduleMode === 'now'
      });

      // Clear localStorage
      localStorage.removeItem("draftCampaignId");
      localStorage.removeItem("draftAudienceId");
      localStorage.removeItem("campaignId");
      localStorage.removeItem("audienceId");

    } else {
      toast.error(response?.detail || "Failed to launch campaign");
      setIsLoading(false);
    }
  };

  // Create a template object for preview
  const previewTemplate: Template | null = campaignData.content?.template_id ? {
    id: campaignData.content.template_id,
    name: campaignData.content.template_name || "Custom Template",
    type: campaignData.content.header_type === "image" ? "Media + Text" : "Text",
    preview: campaignData.content.message || campaignData.messages || "Your message preview"
  } : null;

  return (
    <div className="min-h-screen flex items-start justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Form panel ── */}
          <div className="flex-1 rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Schedule and Launch</h1>
              <p className="text-sm text-gray-500 mt-1">
                We have pre-filled this based on your brief. You may edit anything or proceed.
              </p>
            </div>

            <Divider />

            {/* Broadcast info rows */}
            <div className="space-y-6">
              <InfoRow
                left={{ label: 'Broadcast Name', value: broadcastName }}
                right={{ label: 'Broadcast Goal', value: broadcastGoal }}
              />
              <Divider />
              <InfoRow
                left={{
                  label: 'Channels',
                  value: (
                    <div className="flex items-center gap-2">
                      <ChannelIcon channel={channel} />
                      <span className="capitalize">{channel}</span>
                    </div>
                  ),
                }}
                right={{ label: 'Sender Profile', value: senderProfile }}
              />
              <Divider />
              <InfoRow
                left={{ label: 'Total Cost', value: 0||totalCost }}
                right={{ label: 'Estimated Reach', value: 0|| estimatedReach }}
              />
              {walletBallance && (
                <>
                  <Divider />
                  <InfoRow
                    left={{
                      label: 'Wallet Balance',
                      value: formatMoney(Number(walletBallance?.balance))
                    }}
                    right={{
                      label: 'Cost',
                      // value: formatMoney(Number(walletBallance?.balance) - Number(estimatedCost))
                      value: 0
                    }}
                  />
                </>
              )}
            </div>

            <Divider />

            <BroadcastTags tags={tags} onChange={setTags} />

            <Divider />

            <ScheduleOptions
              mode={scheduleMode}
              onModeChange={setScheduleMode}
              selectedDateTime={selectedDateTime}
              onDateTimeChange={setSelectedDateTime}
              timeZone={timeZone}
              onTimeZoneChange={setTimeZone}
            />

            <Divider />

            <FormActions
              onBack={onBack}
              onSaveAsDraft={handleSaveAsDraft}
              onScheduleAndLaunch={handleLaunch}
              isLoading={isLoading}
            />
          </div>

          {/* ── Live Preview panel ── */}
          <div className="w-full lg:w-80 xl:w-96">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 h-full">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Live Preview
              </p>
              <div className="flex items-center justify-center min-h-[480px]">
                {previewTemplate ? (
                  <PhoneMockup
                    template={previewTemplate}
                    vars={{
                      customerName: campaignData.content?.variables?.["1"] || "First_Name",
                      saleName: campaignData.content?.variables?.["2"] || "",
                      discount: campaignData.content?.variables?.["3"] || "",
                      promoCode: campaignData.content?.variables?.["4"] || "",
                      expiryDate: campaignData.content?.variables?.["5"] || "",
                    }}
                    cta={{
                      label: campaignData.content?.buttons?.[0]?.text || "Learn More",
                      url: campaignData.content?.buttons?.[0]?.url || campaignData.link || ""
                    }}
                    mediaFile={campaignData.content?.header_value ? {
                      name: "header-media",
                      type: campaignData.content.header_type === "image" ? "image/jpeg" : "video/mp4"
                    } as File : null}
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <p>No preview available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default ScheduleLaunch